import { useRef, type KeyboardEvent } from 'react';

export interface SidePanelTab<T extends string> {
  id: T;
  label: string;
  testId?: string;
}

interface SidePanelTabsProps<T extends string> {
  tabs: readonly SidePanelTab<T>[];
  active: T;
  onChange: (id: T) => void;
  /** What the tabs choose between, for screen readers. */
  ariaLabel?: string;
}

/**
 * The floor plan's side-panel tabs — Guests, Properties, Chairs. A tablist:
 * the active tab is the one in the tab order, and Left/Right (wrapping),
 * Home and End move between tabs. Those keys are kept from the page, whose
 * arrow keys would otherwise nudge the selected table as well.
 */
export function SidePanelTabs<T extends string>({
  tabs,
  active,
  onChange,
  ariaLabel = 'Side panel',
}: SidePanelTabsProps<T>) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const last = tabs.length - 1;
    const next =
      e.key === 'ArrowRight'
        ? index === last
          ? 0
          : index + 1
        : e.key === 'ArrowLeft'
          ? index === 0
            ? last
            : index - 1
          : e.key === 'Home'
            ? 0
            : e.key === 'End'
              ? last
              : null;
    if (next === null) return;
    e.preventDefault();
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
    onChange(tabs[next].id);
    refs.current[next]?.focus();
  };

  return (
    <div className="flex flex-wrap gap-1 mb-3" role="tablist" aria-label={ariaLabel}>
      {tabs.map((tab, i) => {
        const on = tab.id === active;
        return (
          <button
            key={tab.id}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="button"
            role="tab"
            aria-selected={on}
            tabIndex={on ? 0 : -1}
            onClick={() => onChange(tab.id)}
            onKeyDown={(e) => onKeyDown(e, i)}
            className={`px-2 py-1 text-xs rounded ${
              on ? 'bg-primary-100 text-primary-700 font-medium' : 'text-gray-500 hover:bg-gray-100'
            }`}
            data-testid={tab.testId}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}
